import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FooterSection from "../home/components/FooterSection";

const sections = [
  {
    id: "behandlingsansvarlig",
    title: "1. Behandlingsansvarlig",
    content: `ExoMed er behandlingsansvarlig for personopplysningene vi samler inn og behandler i forbindelse med drift av våre tjenester og dette nettstedet.

Kontaktinformasjon:
E-post: kontakt@exomed.no
Telefon: 51 53 51 53`,
  },
  {
    id: "hvilke-opplysninger",
    title: "2. Hvilke personopplysninger behandler vi?",
    content: `Vi kan behandle følgende kategorier av personopplysninger:

• Kontaktinformasjon: navn, e-postadresse og telefonnummer som du selv oppgir via kontaktskjemaet på nettstedet.
• Henvendelsesinnhold: informasjon du deler i meldingsfeltet når du tar kontakt med oss.
• Tekniske data: IP-adresse, nettlesertype og besøksdata som samles inn automatisk ved bruk av nettstedet.

Vi samler ikke inn sensitive personopplysninger (særlige kategorier etter GDPR artikkel 9) gjennom nettstedet.`,
  },
  {
    id: "formal-og-grunnlag",
    title: "3. Formål og rettslig grunnlag for behandling",
    content: `Vi behandler personopplysninger for følgende formål:

Besvare henvendelser (kontaktskjema)
Rettslig grunnlag: Berettiget interesse (GDPR art. 6 nr. 1 bokstav f) – for å kunne svare på spørsmål og forespørsler fra deg.

Drift og forbedring av nettstedet
Rettslig grunnlag: Berettiget interesse (GDPR art. 6 nr. 1 bokstav f) – for å sikre teknisk funksjonalitet og brukeropplevelse.

Overholdelse av lovpålagte plikter
Rettslig grunnlag: Rettslig forpliktelse (GDPR art. 6 nr. 1 bokstav c).`,
  },
  {
    id: "lagring-og-sletting",
    title: "4. Lagring og sletting",
    content: `Vi lagrer personopplysninger kun så lenge det er nødvendig for å oppfylle formålet med behandlingen, eller så lenge det er påkrevd etter gjeldende lovgivning.

• Henvendelser via kontaktskjema lagres i inntil 12 måneder etter siste kommunikasjon.
• Tekniske loggdata slettes normalt innen 90 dager.

Etter utløp av oppbevaringsperioden vil opplysningene bli slettet på en sikker måte.`,
  },
  {
    id: "dine-rettigheter",
    title: "5. Dine rettigheter",
    content: `Som registrert har du følgende rettigheter i henhold til GDPR:

• Rett til innsyn: Du kan be om en kopi av personopplysningene vi har registrert om deg.
• Rett til retting: Du kan kreve at uriktige eller ufullstendige opplysninger rettes.
• Rett til sletting: Du kan i visse tilfeller kreve at opplysninger om deg slettes («retten til å bli glemt»).
• Rett til begrensning av behandling: Du kan be om at behandlingen av dine opplysninger begrenses i bestemte situasjoner.
• Rett til dataportabilitet: Du kan be om å motta opplysninger du har gitt oss i et strukturert, maskinlesbart format.
• Rett til å protestere: Du kan protestere mot behandling basert på berettiget interesse.

For å utøve dine rettigheter, ta kontakt med oss på kontakt@exomed.no. Vi vil besvare din henvendelse innen 30 dager.`,
  },
  {
    id: "informasjonskapsler",
    title: "6. Informasjonskapsler (cookies)",
    content: `Nettstedet kan benytte teknisk nødvendige informasjonskapsler for å sikre grunnleggende funksjonalitet. Vi benytter ikke sporings- eller markedsføringskapsler uten ditt samtykke.

Ved å bruke nettstedet aksepterer du bruk av teknisk nødvendige informasjonskapsler. Du kan deaktivere informasjonskapsler i nettleserinnstillingene, men dette kan påvirke funksjonaliteten på nettstedet.`,
  },
  {
    id: "tredjeparter",
    title: "7. Deling med tredjeparter",
    content: `Vi deler ikke dine personopplysninger med uvedkommende tredjeparter. Vi kan benytte databehandlere (leverandører) som behandler data på våre vegne, for eksempel hosting- og skytjenester. Alle slike leverandører er bundet av databehandleravtaler i henhold til GDPR.

Vi overfører ikke personopplysninger til land utenfor EØS uten at det foreligger et tilstrekkelig grunnlag i henhold til GDPR kapittel V.`,
  },
  {
    id: "sikkerhet",
    title: "8. Informasjonssikkerhet",
    content: `Vi iverksetter tekniske og organisatoriske tiltak for å beskytte personopplysningene dine mot uautorisert tilgang, endring, spredning eller sletting. Tilgang til personopplysninger begrenses til ansatte og samarbeidspartnere som har tjenstlig behov.`,
  },
  {
    id: "klage",
    title: "9. Klagerett",
    content: `Dersom du mener at vår behandling av personopplysninger er i strid med personvernregelverket, har du rett til å klage til Datatilsynet.

Datatilsynet
Telefon: 22 39 69 00
E-post: postkasse@datatilsynet.no
Nettsted: www.datatilsynet.no`,
  },
  {
    id: "endringer",
    title: "10. Endringer i personvernerklæringen",
    content: `Vi forbeholder oss retten til å oppdatere denne personvernerklæringen ved behov. Vesentlige endringer vil bli kunngjort på nettstedet. Dato for siste oppdatering er angitt øverst på denne siden.`,
  },
];

export default function PersonvernPage() {
  const { t, i18n } = useTranslation();
  const lastUpdated = "21. mars 2026";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isNorwegian = i18n.language.startsWith("nb") || i18n.language === "no";

  return (
    <div className="min-h-screen bg-[#F8F7F4]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Simple page header */}
      <header className="bg-[#F8F7F4]/95 backdrop-blur-sm border-b border-[#E8E4DC] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-8 flex items-center justify-between h-20">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/393595e6-d208-41ce-a3f5-17096d7bbb19.png"
              alt="ExoMed"
              className="h-9 w-auto object-contain"
            />
          </Link>
          <div className="flex items-center gap-6">
            {/* Language toggle */}
            <div className="flex items-center rounded-full p-0.5 border border-[#E8E4DC]">
              {(["en", "nb"] as const).map((lang) => {
                const isActive = lang === "nb" ? isNorwegian : !isNorwegian;
                return (
                  <button
                    key={lang}
                    onClick={() => i18n.changeLanguage(lang)}
                    className={`px-3.5 py-1.5 rounded-full text-[11px] tracking-widest uppercase font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? "bg-[#3D6B5A] text-white"
                        : "text-[#6B6B65] hover:text-[#3D6B5A]"
                    }`}
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {lang === "en" ? "EN" : "NO"}
                  </button>
                );
              })}
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 text-[13px] tracking-widest uppercase font-medium text-[#4A4840] hover:text-[#3D6B5A] transition-colors duration-300 cursor-pointer whitespace-nowrap"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <i className="ri-arrow-left-line text-base" />
              {t("personvern.backToHome")}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#EBF2EE] border-b border-[#D4E3DC] py-20 px-8">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-4 mb-5">
            <div className="w-8 h-px bg-[#3D6B5A]" />
            <span
              className="text-[11px] tracking-[0.25em] uppercase text-[#3D6B5A] font-medium"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t("personvern.legal")}
            </span>
          </div>
          <h1
            className="text-5xl font-light text-[#1A1A18] mb-5 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            {t("personvern.title")}
          </h1>
          <p
            className="text-[#6B6B65] text-[15px] font-light leading-relaxed max-w-xl"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t("personvern.subtitle")}
          </p>
          <p
            className="mt-4 text-[13px] text-[#9B9A94] font-light"
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {t("personvern.lastUpdated")}{" "}
            <strong className="font-medium text-[#6B6B65]">{lastUpdated}</strong>
          </p>
        </div>
      </section>

      {/* Content — always in Norwegian (legal document) */}
      <section className="py-20 px-8">
        <div className="max-w-3xl mx-auto">
          {/* Table of contents */}
          <div className="bg-white border border-[#E8E4DC] rounded-lg p-8 mb-14">
            <h2
              className="text-[11px] tracking-[0.22em] uppercase text-[#3D6B5A] font-medium mb-5"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t("personvern.contents")}
            </h2>
            <ol className="space-y-2.5">
              {sections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-[14px] font-light text-[#4A4840] hover:text-[#3D6B5A] transition-colors duration-200 cursor-pointer"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          <div className="space-y-14">
            {sections.map((s) => (
              <div key={s.id} id={s.id} className="scroll-mt-24">
                <h4
                  className="text-2xl font-light text-[#1A1A18] mb-5 pb-4 border-b border-[#E8E4DC]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "26px" }}
                >
                  <a href={`#${s.id}`}>{s.title}</a>
                </h4>
                <div className="space-y-3">
                  {s.content.split("\n\n").map((paragraph, i) => (
                    <p
                      key={i}
                      className="text-[#5A5A54] text-[15px] font-light leading-relaxed whitespace-pre-line"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Contact box */}
          <div className="mt-16 bg-[#EBF2EE] border border-[#C8D8D3] rounded-lg p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 flex items-center justify-center bg-[#3D6B5A] rounded-md">
                <i className="ri-shield-user-line text-white text-base" />
              </div>
              <h3
                className="font-light text-[#1A1A18]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "22px" }}
              >
                {t("personvern.contactQ")}
              </h3>
            </div>
            <p
              className="text-[#5A5A54] text-[14px] font-light leading-relaxed mb-5"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              {t("personvern.contactD")}
            </p>
            <div className="flex flex-wrap gap-6">
              <a
                href="mailto:kontakt@exomed.no"
                rel="nofollow"
                className="flex items-center gap-2 text-[#3D6B5A] text-[14px] font-medium hover:underline cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-mail-line text-base" />
                kontakt@exomed.no
              </a>
              <a
                href="tel:51535153"
                className="flex items-center gap-2 text-[#3D6B5A] text-[14px] font-medium hover:underline cursor-pointer whitespace-nowrap"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                <i className="ri-phone-line text-base" />
                51 53 51 53
              </a>
            </div>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
